/**
 * ObjectiveReplacerModal Component
 *
 * Modal for selecting objectives to add/replace in ObjectivesSelectionStep.
 * Features filtering by status, type, and universal search (name, description, type, tags).
 * Supports inline creation via nested ObjectiveModal.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  Card,
  CardBody,
  Spinner,
  useDisclosure,
  EmptyState,
} from '@enfomentis/ui';
import { Search, Plus, X, AlertTriangle, Check } from 'lucide-react';

// 3. Services & Types
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import { tradingObjectiveSettingsService } from '@/services/TradingObjectiveSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { ObjectiveType } from '@/types/schema/TradingObjectiveSettings';
import { isEntityActive } from '@/types/shared/EntityStatus';

// 4. Local imports
import { ObjectiveModal } from '@/components/Settings/TradingWorkflow/Objectives/ObjectiveModal';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for ObjectiveReplacerModal
 */
export interface ObjectiveReplacerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when an objective is selected */
  onSelectObjective: (objective: TradingObjective) => void;
  /** Set of objective IDs that are already selected (for duplicate prevention) */
  selectedObjectiveIds: Set<string>;
  /** Existing tags from all objectives for ObjectiveModal autocomplete */
  existingTags: string[];
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * ObjectiveRow - Individual objective in the list
 */
interface ObjectiveRowProps {
  objective: TradingObjective;
  typeName: string;
  isAlreadySelected: boolean;
  onSelect: (objective: TradingObjective) => void;
}

const ObjectiveRow: React.FC<ObjectiveRowProps> = ({
  objective,
  typeName,
  isAlreadySelected,
  onSelect,
}) => {
  const isInactive = !isEntityActive(objective.status);
  const tags = objective.tags ?? [];

  return (
    <Card
      isPressable={!isAlreadySelected}
      isHoverable={!isAlreadySelected}
      shadow="sm"
      radius="md"
      fullWidth
      className={`cursor-pointer transition-colors ${
        isAlreadySelected
          ? 'opacity-50 cursor-not-allowed'
          : isInactive
            ? 'border-warning/50'
            : 'hover:bg-content2'
      }`}
      onPress={() => !isAlreadySelected && onSelect(objective)}
    >
      <CardBody className="py-2 px-3">
        <div className="flex-1 min-w-0">
          {/* Row 1: Name • Type • Description (desktop) • Status/Selected badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-text-primary truncate">
              {objective.name}
            </span>
            <span className="text-text-tertiary">•</span>
            <span className="uppercase text-sm text-text-secondary truncate">
              {typeName}
            </span>
            {objective.description && (
              <>
                <span className="text-text-tertiary hidden md:inline">•</span>
                <span className="text-sm text-text-secondary truncate hidden md:inline max-w-xs">
                  {objective.description}
                </span>
              </>
            )}
            {isInactive && (
              <Chip size="sm" variant="flat" color="warning" startContent={<AlertTriangle size={12} />}>
                {objective.status}
              </Chip>
            )}
            {isAlreadySelected && (
              <Chip size="sm" variant="flat" color="default" startContent={<Check size={12} />}>
                Selected
              </Chip>
            )}
          </div>

          {/* Row 2: Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} size="sm" variant="flat" color="default" className="text-xs">
                  {tag}
                </Chip>
              ))}
              {tags.length > 3 && (
                <Chip size="sm" variant="flat" color="default" className="text-xs">
                  +{tags.length - 3}
                </Chip>
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const ObjectiveReplacerModal: React.FC<ObjectiveReplacerModalProps> = ({
  isOpen,
  onClose,
  onSelectObjective,
  selectedObjectiveIds,
  existingTags,
}) => {
  // Data state
  const [allObjectives, setAllObjectives] = useState<TradingObjective[]>([]);
  const [objectiveTypes, setObjectiveTypes] = useState<ObjectiveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

  // Nested modal state
  const objectiveModalDisclosure = useDisclosure();

  // Type lookup map
  const typeMap = useMemo(
    () => new Map(objectiveTypes.map((t) => [t.id, t.name])),
    [objectiveTypes]
  );

  // Get type name for display
  const getTypeName = useCallback(
    (typeId: string) => typeMap.get(typeId) ?? 'Unknown',
    [typeMap]
  );

  // Load data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [objectives, settings] = await Promise.all([
        tradingObjectiveService.getAll(),
        tradingObjectiveSettingsService.getOrCreateDefaultSettings(),
      ]);
      setAllObjectives(objectives);
      setObjectiveTypes(settings.objectiveTypes);
    } catch (error) {
      console.error('Failed to load objectives:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount and subscribe to changes
  useEffect(() => {
    if (isOpen) {
      loadData();
      // Reset filters when modal opens
      setSearchQuery('');
      setShowAllStatuses(false);
      setSelectedTypes(new Set());
    }
  }, [isOpen, loadData]);

  // Subscribe to objective changes for cross-tab sync
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = syncCoordinatorService.subscribe(
      'trading_objectives',
      () => {
        loadData();
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isOpen, loadData]);

  // Filtered objectives
  const filteredObjectives = useMemo(() => {
    let result = allObjectives;

    // Status filter
    if (!showAllStatuses) {
      result = result.filter((obj) => isEntityActive(obj.status));
    }

    // Universal search filter (name, description, type, tags)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((obj) => {
        const typeName = typeMap.get(obj.type)?.toLowerCase() ?? '';
        return (
          obj.name.toLowerCase().includes(query) ||
          obj.description?.toLowerCase().includes(query) ||
          typeName.includes(query) ||
          obj.tags.some((tag) => tag.toLowerCase().includes(query))
        );
      });
    }

    // Type filter (OR logic)
    if (selectedTypes.size > 0) {
      result = result.filter((obj) => selectedTypes.has(obj.type));
    }

    // Sort: active first, then by name (spread to avoid mutating state)
    return [...result].sort((a, b) => {
      const aActive = isEntityActive(a.status);
      const bActive = isEntityActive(b.status);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [allObjectives, showAllStatuses, searchQuery, selectedTypes, typeMap]);

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery.trim() ||
    showAllStatuses ||
    selectedTypes.size > 0;

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setShowAllStatuses(false);
    setSelectedTypes(new Set());
  };

  // Handle type filter change
  const handleTypeFilterChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') {
      setSelectedTypes(new Set());
    } else {
      setSelectedTypes(new Set(Array.from(keys) as string[]));
    }
  };

  // Handle objective selection
  const handleSelect = (objective: TradingObjective) => {
    // Guard against already-selected (race condition)
    if (selectedObjectiveIds.has(objective.id)) {
      return;
    }
    onSelectObjective(objective);
    onClose();
  };

  // Handle new objective created via nested modal
  const handleNewObjectiveSaved = (newObjective?: TradingObjective) => {
    if (newObjective) {
      // Auto-select the new objective
      onSelectObjective(newObjective);
      onClose();
    }
    objectiveModalDisclosure.onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        placement="top-center"
        scrollBehavior="inside"
        classNames={{
          wrapper: 'items-start',
          base: 'my-auto max-h-[90vh]',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-xl font-semibold">Select Objective</h3>
              <p className="text-sm text-text-secondary font-normal">
                Choose from your objective library or create a new one
              </p>
            </div>
            <Button
              color="primary"
              size="sm"
              startContent={<Plus size={16} />}
              onPress={objectiveModalDisclosure.onOpen}
            >
              Add New
            </Button>
          </ModalHeader>

          <ModalBody className="gap-4">
            {/* Search Input */}
            <Input
              placeholder="Search by name, description, type, or tags..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={<Search size={16} className="text-text-secondary" />}
              isClearable
              onClear={() => setSearchQuery('')}
            />

            {/* Filter Row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Type Filter */}
              {objectiveTypes.length > 0 && (
                <Select
                  label="Type"
                  labelPlacement="outside-left"
                  placeholder="All types"
                  size="sm"
                  selectionMode="multiple"
                  selectedKeys={selectedTypes}
                  onSelectionChange={handleTypeFilterChange}
                  className="w-40"
                  classNames={{
                    trigger: 'min-h-unit-8 h-unit-8',
                    label: 'text-sm uppercase'
                  }}
                >
                  {objectiveTypes.map((type) => (
                    <SelectItem key={type.id}>{type.name}</SelectItem>
                  ))}
                </Select>
              )}
              {/* Status Toggle */}
              <Chip
                color={showAllStatuses ? 'default' : 'primary'}
                variant={showAllStatuses ? 'bordered' : 'solid'}
                className="cursor-pointer"
                onClick={() => setShowAllStatuses(!showAllStatuses)}
              >
                {showAllStatuses ? 'All Statuses' : 'Active Only'}
              </Chip>

              

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="light"
                  startContent={<X size={14} />}
                  onPress={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Results Count */}
            <div className="text-sm text-text-secondary">
              {filteredObjectives.length} objective{filteredObjectives.length !== 1 ? 's' : ''} found
              {hasActiveFilters && ' (filtered)'}
            </div>

            {/* Objective List - fixed height container for consistent modal size */}
            <div className="h-[50vh] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <Spinner size="lg" />
                </div>
              ) : allObjectives.length === 0 ? (
                <div className="pt-8">
                  <EmptyState
                    title="No Objectives"
                    description="Create your first objective to get started"
                    action={
                      <Button
                        color="primary"
                        startContent={<Plus size={16} />}
                        onPress={objectiveModalDisclosure.onOpen}
                      >
                        Add First Objective
                      </Button>
                    }
                  />
                </div>
              ) : filteredObjectives.length === 0 ? (
                <div className="pt-4">
                  <EmptyState
                    title="No Matching Objectives"
                    description="Try adjusting your filters"
                    action={
                      <Button variant="flat" onPress={clearFilters}>
                        Clear Filters
                      </Button>
                    }
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredObjectives.map((objective) => (
                    <ObjectiveRow
                      key={objective.id}
                      objective={objective}
                      typeName={getTypeName(objective.type)}
                      isAlreadySelected={selectedObjectiveIds.has(objective.id)}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Nested ObjectiveModal for inline creation */}
      <ObjectiveModal
        isOpen={objectiveModalDisclosure.isOpen}
        onClose={objectiveModalDisclosure.onClose}
        onSave={handleNewObjectiveSaved}
        existingTags={existingTags}
      />
    </>
  );
};

export default ObjectiveReplacerModal;
